import React from 'react';
import { useTranslation } from 'react-i18next';
import { Coins, Repeat, Zap } from 'lucide-react';

// Per-plan commission = 50% of the net (ex-IVA) price.
//   Basic  €1.99/mo  → net €1.64 → €0.82
//   Plus   €2.99/mo  → net €2.47 → €1.24
//   Plus   €29.99/yr → net €24.79 → €12.39
const PLANS = [
  { key: 'basic', nameKey: 'affEarnPlanBasic', nameDefault: 'Basic', price: '€1.99', periodKey: 'affEarnPerMonth', periodDefault: '/month', commission: '€0.82' },
  { key: 'plus_monthly', nameKey: 'affEarnPlanPlus', nameDefault: 'Business Plus', price: '€2.99', periodKey: 'affEarnPerMonth', periodDefault: '/month', commission: '€1.24' },
  { key: 'plus_annual', nameKey: 'affEarnPlanPlus', nameDefault: 'Business Plus', price: '€29.99', periodKey: 'affEarnPerYear', periodDefault: '/year', commission: '€12.40' },
];

export const AffiliateEarnings = () => {
  const { t } = useTranslation();
  return (
    <div className="mt-6 rounded-2xl bg-white border border-purple-200 p-5 shadow-sm" data-testid="affiliate-earnings-panel">
      <div className="flex items-center gap-2 mb-1">
        <Coins className="h-5 w-5 text-purple-600" />
        <h2 className="text-base font-semibold text-gray-900">{t('affEarnTitle', 'What you earn per plan')}</h2>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        {t('affEarnIntro', 'Prices include 21% IVA. Your 50% commission is calculated on the amount excluding IVA. Qualifying subscriptions pay you for life — the first payment and every renewal.')}
      </p>

      <div className="overflow-hidden rounded-xl border border-gray-200">
        <table className="w-full text-sm" data-testid="affiliate-earnings-table">
          <thead className="bg-purple-50 text-purple-800">
            <tr>
              <th className="text-start px-4 py-2 font-semibold">{t('affEarnColPlan', 'Plan')}</th>
              <th className="text-start px-4 py-2 font-semibold">{t('affEarnColPrice', 'Price (IVA incl.)')}</th>
              <th className="text-start px-4 py-2 font-semibold">{t('affEarnColCommission', 'Your commission (per payment)')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {PLANS.map((p) => (
              <tr key={p.key} data-testid={`affiliate-earnings-row-${p.key}`}>
                <td className="px-4 py-2.5 text-gray-800 font-medium">{t(p.nameKey, p.nameDefault)}</td>
                <td className="px-4 py-2.5 text-gray-700">
                  {p.price}<span className="text-gray-400">{t(p.periodKey, p.periodDefault)}</span>
                </td>
                <td className="px-4 py-2.5 font-bold text-purple-700" data-testid={`affiliate-earnings-commission-${p.key}`}>
                  ≈ {p.commission}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex items-start gap-2 text-xs text-gray-600" data-testid="affiliate-earnings-lifetime">
        <Repeat className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
        <span>{t('affEarnLifetimeNote', 'You earn the commission above on their first payment and on every renewal — for as long as they stay subscribed.')}</span>
      </div>
      <div className="mt-2 flex items-start gap-2 text-xs text-gray-600" data-testid="affiliate-earnings-boost">
        <Zap className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
        <span>{t('affEarnBoostNote', 'Boosts & sponsored posts: you also earn 50% of every paid boost or sponsored post a referred business buys.')}</span>
      </div>
    </div>
  );
};

export default AffiliateEarnings;
